/**
 * #272 — the ONE mixed-venue bulk cancel executor, plus the cross-surface
 * Polymarket in-flight fence.
 *
 * @ai-danger Every path that cancels more than one order at a time goes through
 * here. Before #272 there were two: `accountStore.cancelOrder` (fenced, #241)
 * and Position Tracker's inline `sweepCancelOrders`, which called `apiFetch`
 * directly and therefore bypassed the fence entirely — a sweep could re-DELETE
 * an ID a row click already owned. A second sweep implementation is the defect;
 * do not add one.
 *
 * Contract:
 *
 *  - The set is taken from a FROZEN snapshot. This function never reads a live
 *    order list, so orders that appear after the trader confirmed cannot join
 *    the batch.
 *  - Every Polymarket ID in the batch is CLAIMED synchronously, before the first
 *    relay request. Claiming lazily would leave a not-yet-reached ID clickable
 *    mid-sweep, and the row click would race the sweep to the same DELETE.
 *    (Kalshi's equivalent claim lives in `accountStore.cancelOrders`, which owns
 *    #241's state machine.)
 *  - Each venue's requests run sequentially; the two venues run concurrently.
 *    Their rate limits and refreshes are independent, so serializing across
 *    venues would only add latency.
 *  - Each affected venue refreshes EXACTLY ONCE, after its own leg — never once
 *    per order.
 *  - A 2xx is `submitted`, never `cancelled` or `succeeded`. The order is proven
 *    gone only when an authoritative snapshot stops listing it.
 *  - Failures stay per-order, with the venue's raw message, so a retry can be
 *    built from failures ALONE. Retrying a submitted ID resends a cancellation
 *    that already worked and fails misleadingly.
 */

import { cancelPolyOrderViaRelay } from '@/lib/polymarket/relay';
import type { FrozenCancelSnapshot, OrderCancelTarget } from './gameOrderSelection';
import type { KalshiCancelBatchResult } from './kalshiCancelLifecycle';

export type { KalshiCancelBatchResult } from './kalshiCancelLifecycle';

export interface CancelAttemptResult {
  target: OrderCancelTarget;
  /** `submitted` = the venue accepted the DELETE. NOT proof of removal. */
  status: 'submitted' | 'failed' | 'already-pending';
  error: string | null;
}

export interface BulkCancelReport {
  snapshot: FrozenCancelSnapshot;
  results: readonly CancelAttemptResult[];
  submitted: readonly OrderCancelTarget[];
  /** Retry material — and the ONLY retry material. */
  failed: readonly CancelAttemptResult[];
  /** Owned by another surface's in-flight cancel; neither sent nor retryable. */
  alreadyPending: readonly CancelAttemptResult[];
  /** Post-sweep refresh problems. Reported beside the per-order results rather
   *  than replacing them: a failed refresh says nothing about whether the
   *  cancels were accepted. */
  refreshErrors: readonly string[];
  /** The evidence breadcrumb is deliberately non-authoritative and may fail
   *  without blocking venue cancels. Keep that degradation visible. */
  auditError: string | null;
}

export const POLY_CANCEL_ALREADY_PENDING_MESSAGE =
  'Cancel already pending for this Polymarket order.';

export interface PolyCancelOnceResult {
  ok: boolean;
  error?: string;
  alreadyPending?: true;
}

// ── The Polymarket cancel lifecycle fence ───────────────────────────
//
// Module-scoped, and it deliberately outlives the DELETE response — this is
// #241's contract applied to the second venue (Brendan, PR #276 review).
//
// The first cut released the ID in a `finally`, i.e. the moment the request
// settled. Two ways that still permits a duplicate DELETE:
//
//   1. WITHIN a sweep. The venue loop is sequential, so releasing p-1 as soon as
//      it settles leaves p-1 clickable for the rest of the run while p-2..pN are
//      still going out.
//   2. AFTER a 2xx. A 2xx is an accepted request, not a removal. The hook's
//      optimistic row removal is LOCAL to one mount, and its post-cancel refresh
//      can legitimately return CLOB's pre-cancel book (venue propagation lag) —
//      that response is current-generation and current-identity, so every mount
//      re-renders the order. Unfenced, the next click re-DELETEs an order the
//      venue already accepted a cancel for, and it fails misleadingly *after*
//      the first cancel worked. That is precisely the #241 defect.
//
// So an accepted ID stays fenced until a CURRENT-IDENTITY authoritative
// open-orders snapshot no longer lists it. Three ways out, and only three:
// authoritative removal, a failed request (nothing was accepted, so it is
// immediately retryable), or an identity/reset boundary.
type PolyCancelPhase =
  /** DELETE is in flight. */
  | 'requesting'
  /** DELETE returned 2xx; waiting for the ID to leave the authoritative list. */
  | 'awaiting-removal';

interface PolyCancelEntry {
  phase: PolyCancelPhase;
  /** The account this claim belongs to; a snapshot for another account is not
   *  authority over it. */
  identity: string | null;
  timer: ReturnType<typeof setTimeout> | null;
}

const _polyCancels = new Map<string, PolyCancelEntry>();
let _polyFenceIdentity: string | null = null;

/** Bounded escape from a stalled synchronization, mirroring
 *  `KALSHI_CANCEL_SYNC_TIMEOUT_MS`. Without it a snapshot that never drops the
 *  ID (dead poll, venue that keeps listing it) would wedge the row forever.
 *  Releasing is NOT a claim that the order survived — only that we can no longer
 *  confirm, and the trader gets the control back. */
export const POLY_CANCEL_SYNC_TIMEOUT_MS = 5_000;

function clearPolyTimer(entry: PolyCancelEntry | undefined): void {
  if (entry?.timer) clearTimeout(entry.timer);
}

/** Drop one ID's fence. Used for a failed request and for the timeout escape. */
function releasePolyCancel(orderId: string): void {
  const entry = _polyCancels.get(orderId);
  if (!entry) return;
  clearPolyTimer(entry);
  _polyCancels.delete(orderId);
}

/** True while a cancel for this order is in flight OR accepted-but-unconfirmed. */
export function isPolyCancelPending(orderId: string): boolean {
  return _polyCancels.has(orderId);
}

/**
 * Bind the fence to an account. Switching identity clears every entry: those
 * claims describe another account's orders, and a snapshot for the new account
 * must never be treated as authority over them (nor they over it).
 */
export function setPolyCancelIdentity(identity: string | null): void {
  if (identity === _polyFenceIdentity) return;
  for (const entry of _polyCancels.values()) clearPolyTimer(entry);
  _polyCancels.clear();
  _polyFenceIdentity = identity;
}

/**
 * The authoritative step, mirroring `reconcileKalshiCancels`. Given the order-ID
 * set of a successfully parsed, current-identity open-orders snapshot, drop every
 * fenced ID the venue no longer lists — that disappearance is the only real
 * cancellation confirmation.
 *
 * Call this ONLY for a real fetch result. The hook's OPTIMISTIC local removal
 * must never reach here: it is this client's guess, not the venue's answer, and
 * treating it as authority would reopen the duplicate window immediately.
 */
export function reconcilePolyCancels(
  authoritativeOrderIds: Iterable<string>,
  identity: string | null,
): void {
  if (_polyCancels.size === 0) return;
  if (identity !== _polyFenceIdentity) return;
  const live = new Set<string>();
  for (const id of authoritativeOrderIds) {
    if (typeof id === 'string' && id.trim() !== '') live.add(id.trim());
  }
  for (const [orderId, entry] of [..._polyCancels]) {
    // An in-flight request is not yet a claim about the book — a snapshot taken
    // mid-DELETE must not retire it.
    if (entry.phase !== 'awaiting-removal') continue;
    if (entry.identity !== identity) continue;
    if (!live.has(orderId)) releasePolyCancel(orderId);
  }
}

/** Test seam — the fence outlives a component, so a suite must be able to
 *  clear it between cases without reloading the module graph. */
export function _resetPolyCancelFenceForTests(): void {
  for (const entry of _polyCancels.values()) clearPolyTimer(entry);
  _polyCancels.clear();
  _polyFenceIdentity = null;
}

/** Claim a whole Polymarket ID set atomically. Returns the IDs this caller now
 *  owns plus the ones another surface already owns. No awaits — the claim must
 *  complete inside one tick or the gap it leaves is the race it prevents. */
function claimPolyOrderIds(ids: readonly string[]): {
  owned: string[];
  alreadyPending: string[];
} {
  const owned: string[] = [];
  const alreadyPending: string[] = [];
  const seen = new Set<string>();
  for (const raw of ids) {
    const id = typeof raw === 'string' ? raw.trim() : '';
    if (!id || seen.has(id)) continue;
    seen.add(id);
    if (_polyCancels.has(id)) alreadyPending.push(id);
    else {
      _polyCancels.set(id, {
        phase: 'requesting',
        identity: _polyFenceIdentity,
        timer: null,
      });
      owned.push(id);
    }
  }
  return { owned, alreadyPending };
}

/** Release claims the venue loop never got to (it cannot throw today, but an
 *  orphaned `requesting` claim would wedge those rows). Deliberately leaves
 *  `awaiting-removal` entries alone — those are the accepted ones. */
function releaseUnsentPolyClaims(ids: Iterable<string>): void {
  for (const id of ids) {
    if (_polyCancels.get(id)?.phase === 'requesting') releasePolyCancel(id);
  }
}

/** Send the DELETE for an ID this caller ALREADY owns. Never claims — so it
 *  cannot be called on an unowned ID by accident, and the bulk path's up-front
 *  claim stays the single claim point.
 *
 *  On 2xx the fence is PROMOTED, not released: see the block comment above. */
async function sendOwnedPolyCancel(
  orderId: string,
  request: typeof cancelPolyOrderViaRelay,
): Promise<PolyCancelOnceResult> {
  try {
    const out = await request(orderId);
    if (out.ok) {
      const entry = _polyCancels.get(orderId);
      // Only promote an attempt that is still 'requesting': a reset or identity
      // switch during the request must not be undone by a late success.
      if (entry?.phase === 'requesting') {
        entry.phase = 'awaiting-removal';
        entry.timer = setTimeout(() => releasePolyCancel(orderId), POLY_CANCEL_SYNC_TIMEOUT_MS);
      }
      return { ok: true };
    }
    // Nothing was accepted — immediately retryable.
    releasePolyCancel(orderId);
    return { ok: false, error: out.error };
  } catch (err) {
    releasePolyCancel(orderId);
    return { ok: false, error: err instanceof Error && err.message ? err.message : 'Cancel failed' };
  }
}

/**
 * Cancel ONE Polymarket order, at most once concurrently app-wide. The single-row
 * cancel path calls this so a row click and a bulk sweep cannot both submit the
 * same ID.
 */
export async function cancelPolyOrderOnce(
  orderId: string,
  request: typeof cancelPolyOrderViaRelay = cancelPolyOrderViaRelay,
): Promise<PolyCancelOnceResult> {
  const id = typeof orderId === 'string' ? orderId.trim() : '';
  if (!id) return { ok: false, error: 'Order has no ID — cannot cancel.' };
  const { owned } = claimPolyOrderIds([id]);
  if (owned.length === 0) {
    return { ok: false, error: POLY_CANCEL_ALREADY_PENDING_MESSAGE, alreadyPending: true };
  }
  return sendOwnedPolyCancel(id, request);
}

export interface BulkCancelDeps {
  /** The #241-fenced Kalshi batch — `accountStore.cancelOrders`. Pass
   *  `{ refreshAfter: false }` there; this executor owns the venue refresh. */
  cancelKalshiOrders: (ids: readonly string[]) => Promise<readonly KalshiCancelBatchResult[]>;
  requestPolymarketCancel: typeof cancelPolyOrderViaRelay;
  refreshKalshi: () => Promise<unknown>;
  refreshPolymarket: () => Promise<unknown>;
  /** Position Tracker only: durable evidence for the exact frozen set. */
  recordAttempt?: (snapshot: FrozenCancelSnapshot) => Promise<void>;
}

/** Dedupe by `${venue}:${orderId}` — a snapshot built from two overlapping
 *  rendered lists can legitimately repeat a row, and one order must produce one
 *  request. The same textual ID on two venues is NOT a duplicate. */
function dedupeTargets(targets: readonly OrderCancelTarget[]): OrderCancelTarget[] {
  const seen = new Set<string>();
  const out: OrderCancelTarget[] = [];
  for (const t of targets) {
    const key = t.key || `${t.venue}:${t.orderId}`;
    if (!t.orderId || seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

export async function executeBulkOrderCancel(
  snapshot: FrozenCancelSnapshot,
  deps: BulkCancelDeps,
): Promise<BulkCancelReport> {
  const targets = dedupeTargets(snapshot.targets);
  const kalshiTargets = targets.filter((t) => t.venue === 'kalshi');
  const polyTargets = targets.filter((t) => t.venue === 'polymarket');

  const byKey = new Map<string, CancelAttemptResult>();
  const refreshErrors: string[] = [];
  let auditError: string | null = null;

  // Claim the ENTIRE Polymarket set before any await. The audit request below
  // may yield; holding the claims closes the row-click race while it does.
  const polyClaim = claimPolyOrderIds(polyTargets.map((t) => t.orderId));
  const polyOwned = new Set(polyClaim.owned);

  if (deps.recordAttempt) {
    try {
      await deps.recordAttempt(snapshot);
    } catch (err) {
      auditError =
        err instanceof Error && err.message ? err.message : 'Cancel All attempt log unavailable';
    }
  }

  const kalshiLeg = async () => {
    if (kalshiTargets.length === 0) return;
    try {
      const batch = await deps.cancelKalshiOrders(kalshiTargets.map((t) => t.orderId));
      const byId = new Map(batch.map((row) => [row.orderId, row]));
      for (const target of kalshiTargets) {
        const row = byId.get(target.orderId);
        byKey.set(target.key, {
          target,
          // No row back for an ID we asked about means we cannot claim it was
          // submitted. Fail closed so it shows up as retryable.
          status: row?.status ?? 'failed',
          error: row?.error ?? (row ? null : 'Cancel not attempted'),
        });
      }
    } catch (err) {
      const message = err instanceof Error && err.message ? err.message : 'Cancel failed';
      for (const target of kalshiTargets) {
        byKey.set(target.key, { target, status: 'failed', error: message });
      }
    } finally {
      try {
        await deps.refreshKalshi();
      } catch (err) {
        refreshErrors.push(
          `Kalshi: ${err instanceof Error && err.message ? err.message : 'refresh failed'}`,
        );
      }
    }
  };

  const polyLeg = async () => {
    if (polyTargets.length === 0) return;
    try {
      for (const target of polyTargets) {
        if (!polyOwned.has(target.orderId)) {
          byKey.set(target.key, {
            target,
            status: 'already-pending',
            error: POLY_CANCEL_ALREADY_PENDING_MESSAGE,
          });
          continue;
        }
        const out = await sendOwnedPolyCancel(target.orderId, deps.requestPolymarketCancel);
        byKey.set(target.key, {
          target,
          status: out.ok ? 'submitted' : 'failed',
          error: out.ok ? null : out.error ?? 'Cancel failed',
        });
      }
    } finally {
      // Only the claims the loop never sent. An ACCEPTED id stays fenced until
      // an authoritative snapshot drops it — releasing it here would reopen the
      // duplicate window the moment the sweep finished.
      releaseUnsentPolyClaims(polyOwned);
      try {
        await deps.refreshPolymarket();
      } catch (err) {
        refreshErrors.push(
          `Polymarket: ${err instanceof Error && err.message ? err.message : 'refresh failed'}`,
        );
      }
    }
  };

  await Promise.all([kalshiLeg(), polyLeg()]);

  const results = targets.map(
    (target) =>
      byKey.get(target.key) ?? {
        target,
        status: 'failed' as const,
        error: 'Cancel not attempted',
      },
  );

  return {
    snapshot,
    results,
    submitted: results.filter((r) => r.status === 'submitted').map((r) => r.target),
    failed: results.filter((r) => r.status === 'failed'),
    alreadyPending: results.filter((r) => r.status === 'already-pending'),
    refreshErrors,
    auditError,
  };
}
